/**
 * Horizontal ordering — docs/08 §4.2 step 3.
 *
 * Barycentre sweeps over adjacent layers, with **couple groups as atomic units**:
 * a rail that gets split by the ordering pass is a household drawn in two halves,
 * which no amount of edge routing recovers. Children are then ordered by birth
 * under their junction, nulls last — the same order every list in the app uses,
 * so the chart and the profile agree about who is the eldest.
 */

import { fuzzyDateSortKey, type PersonSummary } from '@famtree/shared';
import type { CoupleGroup } from './couples.js';

export interface OrderedLayer {
  generation: number;
  groups: CoupleGroup[];
}

export interface OrderingInput {
  groups: CoupleGroup[];
  persons: Map<string, PersonSummary>;
  /** child id → the ids of its parents, for the barycentre. */
  parentsOf: Map<string, string[]>;
  sweeps: number;
}

export function orderLayers(input: OrderingInput): OrderedLayer[] {
  const byGeneration = new Map<number, CoupleGroup[]>();
  for (const group of input.groups) {
    byGeneration.set(group.generation, [...(byGeneration.get(group.generation) ?? []), group]);
  }

  const generations = [...byGeneration.keys()].sort((a, b) => a - b);
  const layers: OrderedLayer[] = generations.map((generation) => ({
    generation,
    // A deterministic seed: by earliest birth in the group, then by id. Starting
    // from an arbitrary order makes the barycentre result arbitrary too.
    groups: (byGeneration.get(generation) ?? []).sort(
      (x, y) =>
        seed(x, input.persons) - seed(y, input.persons) ||
        x.members[0]!.localeCompare(y.members[0]!),
    ),
  }));

  const positionOf = new Map<string, number>();
  const reindex = (): void => {
    positionOf.clear();
    for (const layer of layers) {
      let index = 0;
      for (const group of layer.groups) {
        for (const member of group.members) positionOf.set(member, index++);
      }
    }
  };
  reindex();

  const barycentre = (group: CoupleGroup, neighbours: (id: string) => string[]): number => {
    const values: number[] = [];
    for (const member of group.members) {
      for (const other of neighbours(member)) {
        const position = positionOf.get(other);
        if (position !== undefined) values.push(position);
      }
    }
    if (values.length === 0) return Number.POSITIVE_INFINITY; // unconstrained: keep put
    values.sort((a, b) => a - b);
    return values[Math.floor(values.length / 2)]!; // median is steadier than the mean
  };

  const childrenOf = new Map<string, string[]>();
  for (const [child, parents] of input.parentsOf) {
    for (const parent of parents) {
      childrenOf.set(parent, [...(childrenOf.get(parent) ?? []), child]);
    }
  }

  for (let sweep = 0; sweep < input.sweeps; sweep += 1) {
    const downward = sweep % 2 === 0;
    const order = downward ? layers : [...layers].reverse();
    for (const layer of order) {
      const neighbours = downward
        ? (id: string) => input.parentsOf.get(id) ?? []
        : (id: string) => childrenOf.get(id) ?? [];
      const scored = layer.groups.map((group, index) => ({
        group,
        index,
        score: barycentre(group, neighbours),
      }));
      // An unconstrained group keeps its slot rather than drifting to one end.
      scored.sort((x, y) => (x.score === y.score ? x.index - y.index : x.score - y.score));
      layer.groups = scored.map((entry) => entry.group);
      reindex();
    }
  }

  return layers;
}

function seed(group: CoupleGroup, persons: Map<string, PersonSummary>): number {
  const keys = group.members
    .map((id) => fuzzyDateSortKey(persons.get(id)?.birth))
    .filter((key): key is number => key != null);
  return keys.length > 0 ? Math.min(...keys) : Number.MAX_SAFE_INTEGER;
}
